import { useEffect, useRef, useState } from 'react';
import { setReducedMotion } from '@/components/feature/hairline/kernel';
import { mountHandoff } from './handoffFigure';
import type { SalState } from './handoffFigure';
import '@/pages/services/components/service-visual.css';
import './sal-handoff.css';

/* Trace copy per *actual* card position (reported by the figure, never ahead of the sheet). */
const TRACE: Record<SalState, { header: string; tag: string; source: string; qualify: string; owner: string }> = {
  new: { header: 'Live / New inquiry', tag: 'New', source: 'Received', qualify: 'Awaiting details', owner: 'Unassigned' },
  qualified: { header: 'Live / Qualified', tag: 'Qualified', source: 'Received', qualify: 'Complete', owner: 'Ready for handoff' },
  assigned: { header: 'Live / Owner assigned', tag: 'Assigned', source: 'Received', qualify: 'Complete', owner: 'Assigned' },
  review: { header: 'Live / Needs review', tag: 'Needs review', source: 'Received', qualify: 'Details required', owner: 'Handoff on hold' },
};

const BUTTONS: [SalState, string][] = [['new', 'New'], ['qualified', 'Qualified'], ['assigned', 'Assigned'], ['review', 'Review']];

/* Manual pick holds the chosen state this long before autoplay resumes. */
const RESUME_MS = 6000;

interface Api { choose: (s: SalState) => void }

/* SAL Lead Handoff Pipeline — ported from SAL_Lead_Handoff_Pipeline v1 (no iframe).
   Autoplay by default; clicking a state previews it, then autoplay resumes. QA ?state= override removed. */
export default function SalHandoff() {
  const stageRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const apiRef = useRef<Api | null>(null);
  const [phase, setPhase] = useState<SalState>('new');

  useEffect(() => {
    const stage = stageRef.current;
    const svg = svgRef.current;
    if (!stage || !svg) return undefined;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handle = mountHandoff({ stage, svg, read: { textContent: 'new' } }, 1);
    let hovered = false;
    let holding = false;
    let timer: number | undefined;

    const sync = () => handle.setAutomatic(!media.matches && !hovered && !holding);
    const onMedia = () => { setReducedMotion(media.matches); sync(); };
    const onEnter = (e: PointerEvent) => { if (e.pointerType === 'mouse') { hovered = true; sync(); } };
    const onLeave = () => { hovered = false; sync(); };

    handle.onState(setPhase);
    onMedia();
    stage.addEventListener('pointerenter', onEnter);
    stage.addEventListener('pointerleave', onLeave);
    media.addEventListener('change', onMedia);

    apiRef.current = {
      choose: (s) => {
        window.clearTimeout(timer);
        holding = true;
        sync();
        handle.setState(s);
        timer = window.setTimeout(() => { holding = false; sync(); }, RESUME_MS);
      },
    };

    return () => {
      apiRef.current = null;
      window.clearTimeout(timer);
      stage.removeEventListener('pointerenter', onEnter);
      stage.removeEventListener('pointerleave', onLeave);
      media.removeEventListener('change', onMedia);
      handle.destroy();
    };
  }, []);

  const t = TRACE[phase];

  return (
    <div className={`sv-root sv-sal state-${phase}`} data-state={phase}>
      <div className="sv-feature">
        <section className="sv-editorial">
          <div>
            <div className="sv-kicker sv-mono"><b>Workflow</b> / Sales operations</div>
            <h3>One lead.<br />A clear next owner.</h3>
            <p className="sv-description">Move each inquiry through qualification to an assigned owner. Incomplete information stays visible for review.</p>
          </div>
          <div className="sv-benefits">
            <div className="sv-benefit"><span className="sv-n">01</span><span>Receive an inquiry</span></div>
            <div className="sv-benefit"><span className="sv-n">02</span><span>Confirm the next step</span></div>
            <div className="sv-benefit"><span className="sv-n">03</span><span>Assign a clear owner</span></div>
          </div>
        </section>

        <section className="sv-system" aria-label="Interactive lead handoff pipeline">
          <header className="sv-system-top">
            <span className="sv-mono sv-kicker">System / Lead handoff</span>
            <span className="sv-live sv-mono"><i />{t.header}</span>
          </header>

          <div ref={stageRef} className="sv-figure-stage sal-stage" data-controlled="true">
            <div className="sal-figure">
              <svg
                ref={svgRef}
                className="sal-svg"
                viewBox="0 60 400 200"
                role="img"
                aria-label="A lead sheet moves from inquiry papers to a qualification clipboard, then into the next owner's open folder."
              />
              <div className="sal-captions">
                <div><small>01 / RECEIVE</small><strong>Lead inquiry</strong></div>
                <div>
                  <small>02 / CONFIRM</small>
                  <strong>Qualification</strong>
                  <span className="sal-review-note">Missing details · on hold</span>
                </div>
                <div><small>03 / HAND OFF</small><strong>Next owner</strong></div>
              </div>
            </div>
          </div>

          <div className="sv-readout" aria-live="polite">
            <div className="sv-readout-top sv-mono">
              <span>Trace / Representative lead</span>
              <span className="sv-tag">{t.tag}</span>
            </div>
            <div className="sv-events">
              <div className="sv-event"><small>001 / INQUIRY</small><span>{t.source}</span></div>
              <div className="sv-event"><small>002 / QUALIFICATION</small><span>{t.qualify}</span></div>
              <div className="sv-event"><small>003 / NEXT OWNER</small><span className="sv-event-state">{t.owner}</span></div>
            </div>
          </div>

          <div className="sv-controls">
            <div className="sv-controls-caption sv-mono">Demo / Lead handoff</div>
            <div className="sv-button-group" role="group" aria-label="Preview lead handoff states">
              {BUTTONS.map(([s, label]) => (
                <button
                  key={s}
                  type="button"
                  className="sv-mode cursor-target"
                  aria-pressed={phase === s}
                  onClick={() => apiRef.current?.choose(s)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}